import {
  Typography,
  Box,
  Grid,
  Paper,
  Avatar,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Rating,
  Dialog,
} from "@mui/material";
import CtBtn from "../CustomTool/CtBtn";
import { ArrowForward, Close, ArrowBack } from "@mui/icons-material";
import { useState } from "react";

const testimonials = [
  {
    name: "Rahul Sharma",
    role: "Store Owner",
    text: "Simplified my billing and GST return workflow — saved hours each week.",
    rating: 5,
    industry: "Retail",
    location: "Mumbai",
    date: "2024-01-15",
  },
  {
    name: "Neha Patel",
    role: "Accountant",
    text: "Powerful reports and easy reconciliation. Exactly what my clients needed.",
    rating: 5,
    industry: "Accounting",
    location: "Delhi",
    date: "2024-01-10",
  },
  {
    name: "Anita Verma",
    role: "Retail Shop Owner",
    text: "Inventory tracking became effortless — no more stock surprises and faster billing.",
    rating: 5,
    industry: "Retail",
    location: "Bangalore",
    date: "2024-01-08",
  },
  {
    name: "Vikram Joshi",
    role: "Accountant",
    text: "Client accounting is cleaner now, GST reports are accurate and easy to export.",
    rating: 5,
    industry: "Accounting",
    location: "Chennai",
    date: "2024-01-05",
  },
  {
    name: "Priya Singh",
    role: "Boutique Owner",
    text: "The mobile app makes billing on-the-go so convenient. My sales have increased!",
    rating: 4,
    industry: "Fashion",
    location: "Hyderabad",
    date: "2024-01-03",
  },
  {
    name: "Rajesh Kumar",
    role: "CA Firm Partner",
    text: "Managing multiple clients is seamless. The bulk operations save us tremendous time.",
    rating: 5,
    industry: "Professional Services",
    location: "Pune",
    date: "2023-12-28",
  },
  {
    name: "Sneha Gupta",
    role: "Restaurant Owner",
    text: "Inventory management for our restaurant has never been easier. Reduced waste by 30%.",
    rating: 5,
    industry: "Hospitality",
    location: "Goa",
    date: "2023-12-25",
  },
  {
    name: "Amit Desai",
    role: "Small Business Owner",
    text: "The GST filing feature is a lifesaver. No more last-minute hassles before deadlines.",
    rating: 4,
    industry: "Manufacturing",
    location: "Ahmedabad",
    date: "2023-12-20",
  },
];

export function TestimonialsPage({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filter, setFilter] = useState("all");

  const filteredTestimonials =
    filter === "all"
      ? testimonials
      : testimonials.filter((t) => t.industry === filter);

  const industries = ["all", ...new Set(testimonials.map((t) => t.industry))];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#f5f7fa', paddingBottom: 3 }
      }}
    >
      <AppBar position="sticky" elevation={2}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Customer Testimonials
          </Typography>
          <IconButton color="inherit" onClick={onClose}>
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box textAlign="center" sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 800,
              mb: 2,
              color: "primary.main",
              fontSize: { xs: "30px", md: "30px" },
            }}
          >
            What Our Customers Say
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              mx: "auto",
              maxWidth: 600,
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            Discover how Rite Account is transforming businesses across India
          </Typography>
        </Box>

        {/* Filter Chips */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Filter by Industry:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: "center",
            }}
          >
            {industries.map((industry) => (
              <Chip
                key={industry}
                label={industry.charAt(0).toUpperCase() + industry.slice(1)}
                onClick={() => setFilter(industry)}
                color={filter === industry ? "primary" : "default"}
                variant={filter === industry ? "filled" : "outlined"}
              />
            ))}
          </Box>
        </Box>

        {/* Testimonials Grid */}
        <Grid container spacing={3}>
          {filteredTestimonials.map((testimonial, idx) => (
            <Grid item xs={12} md={6} lg={4} key={idx}>
              <Paper
                elevation={2}
                sx={{
                  padding: 2,
                  borderRadius: 3,
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                  background: "white",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 56,
                      height: 56,
                      mr: 2,
                      fontSize: "1.25rem",
                    }}
                  >
                    {testimonial.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.role}
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                    >
                      <Rating
                        value={testimonial.rating}
                        readOnly
                        size="small"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {testimonial.date}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Typography
                  variant="body1"
                  sx={{ fontStyle: "italic", mb: 2, lineHeight: 1.6 }}
                >
                  "{testimonial.text}"
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    m: 0,
                    p: 0,
                  }}
                >
                  <Chip
                    label={testimonial.industry}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {testimonial.location}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Stats Section */}
        <Paper
          elevation={0}
          sx={{
            mt: 6,
            p: 4,
            borderRadius: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            textAlign: "center",
          }}
        >
          <Grid container spacing={4} justifyContent={"center"}>
            <Grid item xs={12} md={3}>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                500+
              </Typography>
              <Typography variant="h6">Happy Businesses</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                95%
              </Typography>
              <Typography variant="h6">Customer Satisfaction</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                15+
              </Typography>
              <Typography variant="h6">Industries Served</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                4.9/5
              </Typography>
              <Typography variant="h6">Average Rating</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* CTA Section */}
        <Box textAlign="center" sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Ready to transform your business?
          </Typography>
          <CtBtn
            variant="contained"
            size="large"
            sx={{ borderRadius: 2, px: 4, py: 1.5 }}
            label="Start Free Trial"
            onClick={() => {
              const url = `https://accounts.ravitechworld.com/`;
              window.open(url, "_blank"); // new tab me open karega
            }}
          />
        </Box>
      </Container>
    </Dialog>
  );
}
